import { Size } from '@bms2/shared';
import { Button } from '../controls';
import styles from './ShowMorePager.module.css';

/**
 * 🔒 The Show More pager under a list that reads its next page from the server (T3465): an optional count
 * sentence, the failure sentence when the last read failed, and the button. It replaces three hand-built
 * copies, on the Item's transactions tile and on the Expenses tab's two groups.
 *
 * 🔨 The caller owns the read and clears `failed` when it reloads; the pager holds no state, and it draws
 * nothing when `visible` is false so a caller never wraps it in its own condition.
 */
export interface ShowMorePagerProps {
  visible: boolean;
  /** The button's words, through `t()`. */
  label: string;
  onShowMore: () => void;
  busy?: boolean;
  /** The "n of m" sentence above the button, already filled; absent where the page draws its count elsewhere. */
  countText?: string;
  /** The failure sentence, through `t()`; the pager draws it as an alert only when `failed` is set. */
  failedText?: string;
  failed?: boolean;
  /** Every `data-*` attribute lands on the wrapper verbatim, because a suite reads the pager by it. */
  data?: Record<`data-${string}`, string>;
}

export function ShowMorePager({ visible, label, onShowMore, busy = false, countText, failedText, failed = false, data }: ShowMorePagerProps) {
  if (!visible) return null;
  return (
    <div className={styles.pager} {...data}>
      {countText ? <p className={styles.hint}>{countText}</p> : null}
      {failed && failedText ? <p className={styles.failed} role="alert">{failedText}</p> : null}
      <Button size={Size.Small} text={[label]} actions={[onShowMore]} disabled={busy} />
    </div>
  );
}
